import { create } from "zustand";
import { persist } from "zustand/middleware";

export const RECENT_CLAIM_TTL_MS = 4 * 60 * 60 * 1000;

interface RecentPatientClaimState {
  patientId: string | null;
  claimedAt: number | null;
  setRecentPatientId: (patientId: string) => void;
  clearRecentPatientId: () => void;
  getRecentPatientId: () => string | null;
}

/**
 * Device-local record of the most recently picked Patient on this browser.
 *
 * Firebase phone-auth assigns the same UID to every Patient on a given
 * phone, so neither `Patient.firebase_uid` nor a Firebase custom claim can
 * distinguish "this sign-in event picked patient X" from "this UID has
 * ever picked patient X". This store fills that gap: it survives reloads
 * but expires after 4 hours, so each fresh session must re-pick.
 */
export const useRecentPatientClaimStore = create<RecentPatientClaimState>()(
  persist(
    (set, get) => ({
      patientId: null,
      claimedAt: null,
      setRecentPatientId: (patientId: string) =>
        set({ patientId, claimedAt: Date.now() }),
      clearRecentPatientId: () => set({ patientId: null, claimedAt: null }),
      getRecentPatientId: () => {
        const { patientId, claimedAt } = get();
        if (!patientId || !claimedAt) return null;
        if (Date.now() - claimedAt > RECENT_CLAIM_TTL_MS) return null;
        return patientId;
      },
    }),
    {
      name: "recent-patient-claim",
      partialize: (state) => ({
        patientId: state.patientId,
        claimedAt: state.claimedAt,
      }),
    },
  ),
);
